/**
 * 武将データ（西国ほか）
 *
 * 担当: 毛利元就(石見30)・島津義久(薩摩36)・伊達政宗(陸奥16)・長宗我部元親(伊予38)
 * 伊達政宗は東国の大名だが、ファイル分割の都合でこちらに含めている。
 *
 * 執筆基準は provinces/_reference.ts（尾張・甲斐）に揃える。
 * 史実の扱いは docs/05-学習設計.md §4 に従う。
 *   - 武将を「英雄」「悪」と評価しない。戦闘行為を美化しない
 *   - 合戦だけでなく、検地・法度・治水・交易など領国経営の事実も必ず入れる
 *   - 通説と異説がある事項は caveat に、ゲームの都合による改変は gameNote に書く
 *
 * 【能力について】
 * docs/01-ゲームデザイン.md §8 の能力表は、合戦がサイコロ勝負だった頃の旧仕様を含む。
 * 現在の合戦は軍配勝負（テキサスホールデム、docs/06-賭けと軍配勝負.md）であり、
 * 「サイコロ+1」「敗北時の兵の損失が半分」のように現行ルールで解釈できない能力は、
 * 現行ルールで意味を持つ形（formationMod / holeCardMod / gain / grantGekokujo）に
 * 翻訳してある。翻訳した旨は ability.text と note.gameNote の双方に明記する。
 */

import type { Warlord } from '../types';

/**
 * 毛利元就 — 安芸の国人領主から中国地方の広域を支配下に置いた大名。
 *
 * 旧仕様「銀山の産出2倍。開始時に謀略札を1枚追加」の翻訳:
 *   産出2倍は盤上に倍率の仕組みがないため開始時の銭に、
 *   謀略札はカード1枚に、寡兵で大軍を破った厳島の戦いは下剋上札1枚に置き換えた。
 */
export const MOTONARI: Warlord = {
  id: 'wl:motonari',
  name: '毛利元就',
  reading: 'もうり もとなり',
  clan: '毛利氏',
  color: '#1B3B6F', // 紺青
  startSquare: 30, // 石見
  ability: {
    name: '調略',
    text: '開始時に銭1・カード1枚・下剋上札1枚を得る。石見銀山の富と、婚姻や調略で味方を増やした事績を表す。旧仕様の「銀山の産出2倍・謀略札1枚追加」は、軍配勝負（テキサスホールデム）に倍率の仕組みがないため、この形に読み替えている。下剋上札は、寡兵が大軍を破った厳島の戦いに対応させたもの。',
    rules: [
      {
        when: 'start',
        // 銭2＋札1＋下剋上1 は開始時の能力として盛りすぎた（実測 31.0%）
        effects: [
          { t: 'gain', res: 'zeni', n: 1 },
          { t: 'draw', n: 1 },
          { t: 'grantGekokujo', n: 1 },
        ],
      },
    ],
  },
  note: {
    summary:
      '安芸の国人領主の家に生まれ、婚姻と調略を重ねて中国地方の広い範囲を支配下に置いた大名。石見銀山の経営でも知られる。',
    detail: {
      1: 'いまの広島県の西がわ、安芸の小さな武士の家に生まれた大名です。ほかの家と縁組みをしたり、話し合いや計略で味方をふやしたりして、中国地方の広いはんいをおさめました。銀のとれる石見銀山も手に入れました。',
      2: '現在の広島県西部にあたる安芸国の国人領主・毛利氏の当主。1523年に家督を継ぎ、1540〜41年の吉田郡山城の戦いで出雲の尼子氏の大軍を退けた。1551年に周防の大内義隆が家臣の陶晴賢に討たれると、1555年の厳島の戦いで陶軍を破り、1557年には大内氏の領国だった周防・長門を併合した（防長経略）。1562年には石見の尼子方勢力を退けて石見銀山を押さえ、1566年に月山富田城の尼子義久を降した。次男を吉川家、三男を小早川家へ養子に出して一族の連携をはかり、1557年には三人の子に「三子教訓状」を書き送っている。',
      3: '安芸国吉田（現在の広島県安芸高田市）を本拠とした国人領主・毛利氏の当主。当初は数千貫規模の在地領主にすぎず、大内氏と尼子氏という二大勢力の間で去就を選ぶ立場にあった。1523年に家督を継ぎ、1540〜41年の吉田郡山城の戦いで尼子晴久の軍を退けたことで大内方における地位を固める。1551年の大寧寺の変で大内義隆が陶晴賢に討たれると、1555年の厳島の戦いで陶軍を破り、1557年までに周防・長門を併合した（防長経略）。1562年には石見の尼子方を退けて石見銀山を掌握するが、降伏した国人・本城常光を誅殺したことで周辺の国人が離反し、出雲の平定には1566年まで4年を要している。石見銀山では灰吹法による精錬で銀の産出が伸び、その銀は大陸との交易に流れた。支配の手法としては、次男元春を吉川家、三男隆景を小早川家に入れて一族で要地を押さえる形をとり、瀬戸内では村上氏ら海上勢力と結んで水運を確保した。1557年の「三子教訓状」は兄弟の結束を14か条にわたって説いたもので、亡き妻・妙玖への言及が繰り返し現れる点でも知られる。',
    },
    year: [1497, 1571],
    keywords: ['毛利元就', '安芸', '厳島の戦い', '石見銀山', '三子教訓状', '防長経略'],
    caveat:
      '「三本の矢」の逸話は江戸時代以降に広まったもので、1557年の「三子教訓状」に矢の話は出てきません。厳島の戦いの兵力差（毛利方4千に対し陶方2万など）も軍記物によって数字が異なり、確かなことはわかっていません。',
    gameNote:
      '毛利氏の本拠は安芸国吉田ですが、盤面の40マスに安芸がないため、石見銀山のある石見（30）を開始国にしています。また能力の「銀山の産出2倍・開始時に謀略札1枚追加」は合戦がサイコロ勝負だった頃の仕様です。現在の合戦は軍配勝負（テキサスホールデム）なので、開始時の銭・カード・下剋上札に読み替えました。',
  },
};

/**
 * 島津義久 — 弟たちと領国を分掌し、九州南部から北へ勢力を広げた大名。
 *
 * 旧仕様「合戦に敗北しても兵の損失が半分（釣り野伏せ）」の翻訳:
 *   軍配勝負に「敗北時の損失を半分にする」仕組みがないため、
 *   誘い込んで迎え撃つ戦い方を formationMod（守勢の役を1段階上げる）に置き換えた。
 */
export const YOSHIHISA: Warlord = {
  id: 'wl:yoshihisa',
  name: '島津義久',
  reading: 'しまづ よしひさ',
  clan: '島津氏',
  color: '#9B2D30', // 臙脂
  startSquare: 36, // 薩摩
  ability: {
    name: '釣り野伏せ',
    text: '城のない領国で守るとき、手勢札が1枚増える。守り勝つと兵を2得る。誘い込んで迎え撃つ野戦の戦い方を表す。旧仕様の「敗北しても兵の損失が半分」は、軍配勝負（テキサスホールデム）に損失を半減させる仕組みがないため、この形に読み替えている。',
    // 「守勢で+1」だと武田の甲州法度とまったく同じ数値になってしまう。
    // 釣り野伏せは籠城ではなく野戦の戦法なので、城のない領国に限る。
    // これで北条（城で+1）・武田（守勢一般で+1）・島津（野戦で+1）が分かれる。
    // 野戦の能力は城のない領国でしか働かない。そのぶん開始時の兵を足す
    // 武田と同じ理由（600局で 19.7%）。誘い込んで勝った兵を取り込む形にする
    rules: [{ when: 'winDefend', effects: [{ t: 'gain', res: 'hei', n: 2 }] }],
    holeCardRules: [{ when: 'defendField', n: 1 }],
  },
  note: {
    summary:
      '薩摩・大隅・日向を統一し、弟たちと役割を分けて九州の広い範囲に勢力を及ぼした大名。1587年に豊臣秀吉に降伏した。',
    detail: {
      1: 'いまの鹿児島県、薩摩の大名です。おとうとたちと力をあわせて九州の南半分をまとめ、さらに北へ勢力をのばしました。1587年に豊臣秀吉の大軍が九州にせめてきたときに降参し、かみをそって「龍伯」と名のりました。',
      2: '島津貴久の長男。1566年に家督を継ぎ、弟の義弘・歳久・家久と役割を分けながら領国を広げた。1570年までに薩摩を、1577年までに大隅・日向を加えて三州を統一し、1578年の耳川の戦いで豊後の大友氏を、1584年の沖田畷の戦いで肥前の龍造寺氏を退けて九州の広い範囲に勢力を及ぼした。しかし1587年、豊臣秀吉の九州平定軍に降伏し、剃髪して龍伯と号した。以後は薩摩・大隅などを安堵され、検地や港の管理を通じた領国の立て直しにあたった。義久に男子はなく、娘の亀寿を通じて家督が継がれた。',
      3: '島津氏は薩摩・大隅・日向の守護をつとめた家柄だが、戦国期には分家や国人の自立で分裂していた。父の貴久が薩摩の統一を進め、義久は1566年に家督を継承する。以後は鹿児島（内城）にあって全体の判断を担い、軍事の多くを弟の義弘・歳久・家久に委ねる体制をとった。1570年に薩摩、1574年に肝付氏を降して大隅、1577年に伊東氏を日向から追って三州を統一し、1578年の耳川、1584年の沖田畷で大友・龍造寺の軍を破る。これらの戦いで用いられたとされる「釣り野伏せ」の呼称は後世の記録に由来し、実態は偽装退却と伏兵の組み合わせと見る説が有力である。1587年に豊臣秀吉へ降伏したのち、1592年には朝鮮出兵への不参加や梅北一揆への関与を疑われた弟の歳久が、秀吉の意を受けた義久自身の差し向けた軍に追われて自害した。また島津領では文禄年間に検地が行われて家臣の知行が改められ、これとは別に1599年には家老の伊集院忠棟が忠恒に殺害されるなど、家中の対立も表面化している。領内には1543年に鉄砲が伝わったとされる種子島があり、琉球や明との交易を含め、南方の海に開けた立地が島津氏の経済を支えた。義久に男子はなく、娘の亀寿が義弘の子・忠恒（家久）に嫁ぐ形で家督が継承されている。',
    },
    year: [1533, 1611],
    keywords: ['島津義久', '薩摩', '三州統一', '耳川の戦い', '九州平定', '文禄検地'],
    caveat:
      '「釣り野伏せ」という呼称は後世の記録に見えるもので、同時代の史料でそう呼ばれていたわけではありません。耳川や沖田畷の戦いについても、伏兵戦というより偽装退却の一種と見る説があり、内容の理解は一定していません。',
    gameNote:
      '能力の「合戦に敗北しても兵の損失が半分」は、合戦がサイコロ勝負だった頃の仕様です。現在の軍配勝負（テキサスホールデム、docs/06）には兵の損失を半減させる仕組みがないため、「守勢の役を1段階上げる」に読み替えました。',
  },
};

/**
 * 伊達政宗 — 南奥州に勢力を広げ、のちに仙台の城下町と北上川の改修を進めた大名。
 *
 * 旧仕様「遠隔地の国も維持費なし。第III章から能力が強化」の翻訳:
 *   章ごとに数値が変わる処理を避け、開始時の石高・兵の得に置き換えた。
 */
export const MASAMUNE: Warlord = {
  id: 'wl:masamune',
  name: '伊達政宗',
  reading: 'だて まさむね',
  clan: '伊達氏',
  color: '#4B3C68', // 深紫
  startSquare: 16, // 陸奥
  ability: {
    name: '奥羽の経営',
    text: '開始時に石高1と兵1を得る。京から遠い広大な領国を、維持の手間なくまとめて扱えることを表す。旧仕様の「遠隔地の国も維持費なし・第III章から強化」は、章ごとに数値が変わる処理を避けるため、開始時の一括の得に読み替えている。',
    rules: [
      {
        when: 'start',
        // 開始時の資源としては最も厚く、600局で 35.3% と突出していた
        effects: [
          { t: 'gain', res: 'koku', n: 1 },
          { t: 'gain', res: 'hei', n: 1 },
        ],
      },
    ],
  },
  note: {
    summary:
      '出羽・陸奥にまたがる伊達氏の当主。南奥州に勢力を広げたのち豊臣秀吉に臣従し、のちに仙台の城下町と北上川の改修を進めた。',
    detail: {
      1: 'いまの山形県や宮城県のあたりをおさめた大名です。17歳で家をつぎ、まわりの家とあらそって領地をひろげました。のちに仙台に城と町をつくり、北上川の流れを変える工事をして田をふやしました。',
      2: '出羽・陸奥にまたがる伊達氏の当主。1584年に父の輝宗から家督を継ぎ、1589年の摺上原の戦いで会津の蘆名氏を破って南奥州の広い範囲を支配下に置いた。しかし翌1590年、豊臣秀吉の小田原攻めに参陣して臣従し、会津などは召し上げられた。1600年の関ヶ原の戦いでは徳川方につき、1601年から仙台に城と城下町を築いている。家臣の川村孫兵衛重吉に北上川の改修を命じ、新田を開いて藩の実収を伸ばした。1613年には家臣の支倉常長をスペインとローマへ派遣している（慶長遣欧使節）。',
      3: '伊達氏は鎌倉期以来の陸奥の武士の家で、稙宗と晴宗が争った天文の乱（1542〜48年）で一族が二分された経緯を持つ。政宗は1584年に家督を継ぎ、1585年の人取橋、1589年の摺上原などを経て南奥州に勢力を広げたが、1590年に小田原へ参陣して豊臣秀吉に臣従したが、同年秋に起きた葛西大崎一揆への関与を疑われ、翌1591年に旧葛西大崎領へ移された。1600年の関ヶ原では徳川方に属し、仙台藩（表高62万石）の初代藩主となる。1601年から始まる仙台の建設では、城下の町割を定めて商人・職人を集め、家臣の川村重吉（孫兵衛）らに北上川・迫川の流路改修を命じた。この工事は元和から寛永年間（1616〜1626年頃）に及ぶ長期の事業で、水害を減らすとともに、石巻へ米を運ぶ舟運を整え、新田開発の基礎になった。1613年に支倉常長をスペイン・ローマへ送った慶長遣欧使節は通商を求めたものと見られるが、幕府の禁教政策が強まる中で具体的な成果は上がらなかった。正室の愛姫は三春の田村氏の出身で、実家の田村家の再興は伊達家中の課題として長く残っている。',
    },
    year: [1567, 1636],
    keywords: ['伊達政宗', '陸奥', '摺上原の戦い', '仙台城下町', '北上川改修', '慶長遣欧使節'],
    caveat:
      '「独眼竜」の呼び名は政宗の生前のものではなく、後世に広まった呼称です。母の義姫が政宗を毒殺しようとしたという話や、小田原へ白装束で参陣したという話も後世の記録に載るもので、史料的な裏づけは十分ではありません。',
    gameNote:
      '政宗は1567年の生まれで、本作の第一章（1467年〜）の時点ではまだ生まれていません。8名から1名を選ぶ仕組みの都合で、全員が最初から盤上にいる形にしています。また能力の「遠隔地の国も維持費なし・第III章から強化」は数値の扱いが定まっていないため、開始時の石高と兵に置き換えました。',
  },
};

/**
 * 長宗我部元親 — 土佐から四国の大半へ勢力を広げ、検地と分国法を整えた大名。
 *
 * 旧仕様「石高→兵の変換効率2倍（一領具足）」の翻訳:
 *   軍配勝負では投入兵が4増えるごとに手勢札が1枚増える（docs/06 §4）ため、
 *   兵そのものを与えることで「兵が多い＝札が多い」が現行ルールで成立する。
 */
export const MOTOCHIKA: Warlord = {
  id: 'wl:motochika',
  name: '長宗我部元親',
  reading: 'ちょうそかべ もとちか',
  clan: '長宗我部氏',
  color: '#2E6B4F', // 木賊色
  startSquare: 38, // 伊予（史実の本拠は土佐。gameNote 参照）
  ability: {
    name: '一領具足',
    text: '開始時に兵を2得る。軍配勝負では投入兵が4増えるごとに手勢札が1枚増えるため（docs/06 §4）、兵の多さがそのまま札の多さになる。旧仕様の「石高→兵の変換効率2倍」を、現行ルールで意味を持つ形に置き換えたもの。',
    // 兵+4 は開始時の資源能力として強すぎた（AI 200局で勝率 45.0%）。
    // 席順の補正で後手にも兵が入るようになったぶん、相対的にも効きすぎる。
    rules: [{ when: 'start', effects: [{ t: 'gain', res: 'hei', n: 2 }] }],
  },
  note: {
    summary:
      '土佐の岡豊城を本拠に四国の大半へ勢力を広げた大名。豊臣秀吉に降伏したのち、領内の検地と分国法の制定を進めた。',
    detail: {
      1: 'いまの高知県、土佐の大名です。ふだんは田をたがやし、いくさになると武具を持って集まる「一領具足」とよばれる人たちを兵にして、四国の広いはんいへ勢力をのばしました。のちに豊臣秀吉に降参し、土佐一国をおさめました。',
      2: '土佐の岡豊城を本拠とした長宗我部氏の当主。1560年の長浜の戦いを初陣とし、本山氏・安芸氏を退け、1575年の四万十川の戦いで一条氏を破って土佐をほぼ統一した。半農半兵の「一領具足」と呼ばれる兵を動員して阿波・讃岐・伊予へ進み、1585年には四国の大半に及んだが、同年の豊臣秀吉による四国攻めに降伏し、土佐一国のみを安堵された。1586年の戸次川の戦いでは嫡男の信親を失っている。晩年は領内の検地（長宗我部地検帳）を進め、1597年に分国法「長宗我部元親百箇条」を定めた。',
      3: '長宗我部氏は土佐の国人領主で、父・国親の代に本拠の岡豊城へ復帰した。元親は本山氏・安芸氏・一条氏を破って土佐を統一し、阿波・讃岐へ進出する。動員の基盤とされる「一領具足」は、平時は耕作し、有事に自弁の武具で参陣する在地の武士層を指す語である。1582年の本能寺の変の直前、織田信長は四国政策を転換して元親への圧力を強めていたが、2014年に紹介された「石谷家文書」により、明智光秀の家臣・斎藤利三の兄で、元親の正室の実家である石谷家に入った頼辰が、両者の間を仲介していたことが具体的に確かめられた。本能寺の変の原因を四国政策に求める「四国説」の根拠として注目されている。1585年に秀吉へ降伏したのち、元親・盛親父子は土地を一筆ごとに書き上げた検地帳（長宗我部地検帳）を作成し、1597年には「長宗我部元親百箇条」を定めた。この分国法は神社の祭礼から身分・訴訟・年貢・相続までを扱い、豊臣政権を「公儀」と記す点で、他の戦国大名の分国法とは性格が異なる。地検帳と百箇条はいずれも、近世土佐の土地と身分を知るための基礎史料になっている。',
    },
    year: [1539, 1599],
    keywords: ['長宗我部元親', '土佐', '一領具足', '四国攻め', '長宗我部地検帳', '長宗我部元親百箇条'],
    caveat:
      '「一領具足」という呼称は江戸時代の記録に多く見えるもので、元親の時代にこの言葉と実態がどこまで一致していたかは議論があります。本能寺の変の原因を四国政策に求める「四国説」も有力な説の一つであって、確定した説明ではありません。',
    gameNote:
      '長宗我部氏の本拠は土佐（現在の高知県）ですが、盤面の40マスに土佐がないため、四国を代表するマスとして伊予（38）を開始国にしています。また能力の「石高→兵の変換効率2倍」は、現在の軍配勝負では投入兵が多いほど手勢札が増える（docs/06 §4）ため、開始時の兵として表しました。',
  },
};

export const WEST_WARLORDS: readonly Warlord[] = [MOTONARI, YOSHIHISA, MASAMUNE, MOTOCHIKA];
